import {BrowserModule} from '@angular/platform-browser';
import {CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule} from '@angular/core';

import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {LoginComponent} from './login/login.component';
import {ServiceWorkerModule} from '@angular/service-worker';
import {RegistrationComponent} from './registration/registration.component';
import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';
import {ConnectHttpInterceptor} from './common/connect-http-interceptor';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {AuthGuardService} from './common/auth-guard.service';
import {AuthService} from './common/auth.service';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {ConnectHomeComponent} from './home/connect-home.component';
import {ConnectActivationComponent} from './activation/connect-activation.component';
import {ConnectSharedDocsComponent} from './shared-docs/connect-shared-docs.component';
import {ConnectMaterialModule} from "./connect-material.module";
import {ConnectCirfClientListComponent} from "./cirf/list/connect-cirf-client-list.component";
import {ConnectCirfComponent} from "./cirf/form/connect-cirf.component";
import {DatePipe} from "@angular/common";
import {ConnectHomeListComponent} from "./home/connect-home-list.component";
import {ConfirmDialogComponent} from "./common/confirm-dialog/confirm-dialog.component";
import {ForgotPasswordComponent} from "./forgot-password/forgot-password.component";
import {AcceptTermsConditionsComponent} from './accept-terms-conditions/accept-terms-conditions.component';
import {AcceptTermsConditionsService} from './common/accept-terms-conditions.service';
import {ResetPasswordComponent} from './reset-password/reset-password.component';
import {MatterPropertyInfoComponent} from "./cirf/matter-property-info-form/matter-property-info-component";
import {MortgageFormComponent} from "./cirf/mortgage-form/mortgage-form.component";
import {FileUploadComponent} from "./common/file-upload/file-upload.component";
import {GlobalErrorHandler} from "./common/global-error-handler";
import {NotificationComponent} from "./common/notification/notification.component";
import {AddressFormComponent} from "./common/address-form/address-form.component";
import {AddressSearchService} from "./common/address-form/address-search.service";
import {PhoneNumberDirective} from "./common/directives/phone-number.directive";
import {PrintCirfComponent} from "./cirf/print-cirf/print-cirf.component";
import {IDService} from "./common/id.service";
import {ReferralFormComponent} from "./referral/referral-form.component";
import {RxFormMapperModule} from "rx-form-mapper";
import {CurrencyMaskInputMode, NgxCurrencyModule} from "ngx-currency";
import {DpConnectAutoSuggestComponent} from "./common/auto-suggest/dp-connect-auto-suggest.component";
import {AutofocusDirective} from "./common/autofocus.directive";
import {MatterSummaryComponent} from './matter-summary/matter-summary.component';
import {MatterInfoComponent} from './matter/matter-info/matter-info.component';
import {DpProvinceFieldLabel} from "./common/directives/dp-province-field-label";
import {RecaptchaModule} from 'ng-recaptcha';
import {CirfHomeComponent} from "./cirf/cirf-home.component";
import {BreadcrumbModule} from "xng-breadcrumb";
import {MatterSummaryHomeComponent} from "./matter-summary/matter-summary-home.component";
import {DpTableCommands} from "./common/directives/dp-table-commands";
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from "@angular/material/core";
import {MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS} from '@angular/material-moment-adapter';
import {MultipleFileUploadDialogComponent} from "./common/multiple-file-upload-dialog/multiple-file-upload-dialog.component";
import {DpFieldVisibilityDirective} from "./common/directives/dp-field-visibility.directive";
import {CirfUdfComponent} from "./common/cirf-udf/cirf-udf.component";
import {MyCredentialsComponent} from "./my-credentials/my-credentials.component";
import {FooterComponent} from "./footer/footer.component";
import {CirfSideNavComponent} from "./cirf/cirf-side-nav/cirf-side-nav.component";
import {ClientNameLookupDialogComponent} from "./cirf/cirf-lookup/client-name-lookup-dialog.component";
import {NewMortgageNoteComponent} from "./cirf/new-mortgage-note/new-mortgage-note.component";
import {ExistingMortgageNoteComponent} from "./cirf/existing-mortgage-note/existing-mortgage-note.component";
import {InvoiceListComponent} from "./invoice-list/invoice-list.component";



export const customCurrencyMaskConfig = {
    align: "left",
    allowNegative: false,
    allowZero: true,
    decimal: ".",
    precision: 2,
    prefix: "$ ",
    suffix: "",
    thousands: ",",
    nullable: true,
    min: null,
    max: null,
    inputMode: CurrencyMaskInputMode.NATURAL
};

@NgModule({
    declarations: [
        AppComponent,
        FooterComponent,
        LoginComponent,
        RegistrationComponent,
        ForgotPasswordComponent,
        ConnectHomeComponent,
        ConnectActivationComponent,
        ConnectSharedDocsComponent,
        ConnectCirfComponent,
        ConnectCirfClientListComponent,
        ConnectHomeListComponent,
        ConfirmDialogComponent,
        AcceptTermsConditionsComponent,
        MatterPropertyInfoComponent,
        ResetPasswordComponent,
        MatterPropertyInfoComponent,
        MortgageFormComponent,
        NewMortgageNoteComponent,
        ExistingMortgageNoteComponent,
        FileUploadComponent,
        NotificationComponent,
        AddressFormComponent,
        PhoneNumberDirective,
        AutofocusDirective,
        PrintCirfComponent,
        ReferralFormComponent,
        DpConnectAutoSuggestComponent,
        MatterSummaryComponent,
        MatterInfoComponent,
        DpProvinceFieldLabel,
        CirfHomeComponent,
        MatterSummaryHomeComponent,
        DpTableCommands,
        MultipleFileUploadDialogComponent,
        ClientNameLookupDialogComponent,
        DpFieldVisibilityDirective,
        CirfUdfComponent,
        MyCredentialsComponent,
        CirfSideNavComponent,
        InvoiceListComponent
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        ReactiveFormsModule,
        AppRoutingModule,
        HttpClientModule,
        FormsModule,
        ConnectMaterialModule,
        RxFormMapperModule,
        NgxCurrencyModule.forRoot(customCurrencyMaskConfig),
        ServiceWorkerModule.register('./ngsw-worker.js', {enabled: true}),
        RecaptchaModule,
        BreadcrumbModule
    ],
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ConnectHttpInterceptor,
            multi: true
        },
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        { provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
        },
        { provide: MAT_DATE_FORMATS,
            useValue: {
                parse: { // For Manual user input
                    dateInput: 'DD/MM/YYYY',
                },
                display: {
                    dateInput: 'DD/MM/YYYY',
                    monthYearLabel: 'MMM YYYY',
                    dateA11yLabel: 'LL', //This field is related to Accessibility
                    monthYearA11yLabel: 'MMMM YYYY' //This field is related to Accessibility
                },
            },
        },
        AuthService,
        AuthGuardService,
        DatePipe,
        AcceptTermsConditionsService,
        AddressSearchService,
        IDService
    ],
    entryComponents: [
        ConfirmDialogComponent,
        FileUploadComponent,
        AddressFormComponent,
        MultipleFileUploadDialogComponent,
        ClientNameLookupDialogComponent
    ],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule {
}


